import type { GroupableTurnArtifacts, WireEventType } from "../components/chat/groupAssistantSteps"
import { isCollectInputTool } from "../constants/tools"

export interface StreamedToolEvent {
  type: string
  id?: string
  tool_call_id?: string
  source_tool_call_id?: string
  tool_name?: string
  text?: string
  content?: string
  timestamp?: string
  parent_tool_call_id?: string
  parent_tool_name?: string
  wrapped_type?: WireEventType
  status?: string
  message?: string
  done?: boolean
  started_at?: string
  completed_at?: string
  turnArtifacts?: GroupableTurnArtifacts
}

export interface CollectInputPreviewDelta {
  toolName: string
  toolCallId: string
  argumentsDelta: string
  timestamp: string | undefined
}

export interface StreamedToolResultState {
  content: string
  streaming: boolean | undefined
}

export interface IncomingToolResult extends StreamedToolResultState {
  replacesPrevious: boolean
}

export interface MergedToolResult {
  content: string
  streaming: boolean
}

export function toolResultContentOf(event: StreamedToolEvent): string {
  return event.text || event.content || ""
}

export function toolResultArtifactsOf(event: StreamedToolEvent): GroupableTurnArtifacts {
  return {
    tool_call_id: event.tool_call_id,
    ...(event.source_tool_call_id ? { source_tool_call_id: event.source_tool_call_id } : {}),
    ...(event.parent_tool_call_id ? { parent_tool_call_id: event.parent_tool_call_id } : {}),
    ...(event.parent_tool_name ? { parent_tool_name: event.parent_tool_name } : {}),
    ...(event.wrapped_type ? { wrapped_type: event.wrapped_type } : {}),
    ...(event.status ? { status: event.status } : {}),
    ...(event.message ? { message: event.message } : {}),
    ...(event.done !== undefined ? { done: event.done } : {}),
    ...(event.started_at ? { started_at: event.started_at } : {}),
    ...(event.completed_at ? { completed_at: event.completed_at } : {}),
    ...event.turnArtifacts,
  }
}

export function collectInputPreviewOf(event: StreamedToolEvent): CollectInputPreviewDelta | null {
  if (event.type !== "tool_call_args" || !event.tool_call_id || !event.tool_name || !event.text) return null
  if (!isCollectInputTool(event.tool_name)) return null
  return { toolName: event.tool_name, toolCallId: event.tool_call_id, argumentsDelta: event.text, timestamp: event.timestamp }
}

export function collectInputPreviewArtifacts(toolCallId: string): GroupableTurnArtifacts {
  return { type: "tool_result", tool_call_id: toolCallId, streaming: true }
}

export function mergeStreamedToolResult(existing: StreamedToolResultState, incoming: IncomingToolResult): MergedToolResult {
  const previewEnded = existing.streaming === true && incoming.streaming !== true
  const content = previewEnded || incoming.replacesPrevious ? incoming.content || existing.content : existing.content + incoming.content
  return { content, streaming: incoming.streaming === true }
}

export function isWrappedInterruption(event: StreamedToolEvent): boolean {
  return event.type === "tool_result" && event.wrapped_type === "interrupted"
}

export interface StreamingPreviewArtifacts {
  type?: string
  streaming?: boolean
}

export function withoutStreamingPreviews<Message extends { turnArtifacts?: StreamingPreviewArtifacts }>(messages: Message[]): Message[] {
  return messages.filter((message) => message.turnArtifacts?.streaming !== true)
}

export function opensAssistantBubble(text: string): boolean {
  return text.trim().length > 0
}
